/**
 * As regras e as gravações da tela Enviar documentos (E144.F3, GUS-135), fora do JSX.
 *
 * - **Erros do formulário**: o número fora do CNJ, o CPF sem os verificadores, a identificação
 *   incompleta na abertura do cliente no fluxo eletrônico (E165.F1.T03) e o pedido incoerente (hora
 *   sem data, urgência sem motivo) — os mesmos que o servidor recusa.
 * - **Gravar**: a identificação e o pedido vão ao processo (`PATCH /api/processos`, só o que mudou).
 *   Data do ato, solicitante e espécime saíram da abertura (E163.F1, ADR-0112 §4): ficam na edição
 *   do caso.
 */
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import {
  mudancasDoProcesso,
  validarIdentificacao,
  validarPedido,
  type ErroDoPedido,
  type ErrosDaIdentificacao,
  type FormularioDoEnvio,
} from '@/lib/abertura';
import { CaseError, type CaseRecordData } from '@/lib/cases';
import { atualizarProcesso, lerProcesso, type ProcessoData } from '@/lib/processos';

const t = ptBR.abertura;

export interface ErrosDoFormulario {
  identificacao: ErrosDaIdentificacao;
  pedido: ErroDoPedido | null;
  /** E165.F1.T03: a identificação trava o envio (a área de arquivos e o "Conferir"). */
  identificacaoIncompleta: boolean;
  algum: boolean;
}

/** `obrigatoria`: a abertura do cliente no fluxo eletrônico, num processo novo. */
export function errosDoFormulario(
  formulario: FormularioDoEnvio,
  obrigatoria = false,
): ErrosDoFormulario {
  const identificacao = validarIdentificacao(formulario.identificacao, obrigatoria);
  const pedido = validarPedido(formulario.pedido);
  const identificacaoIncompleta = Object.values(identificacao).some((e) => e !== null);
  return {
    identificacao,
    pedido,
    identificacaoIncompleta,
    algum: identificacaoIncompleta || pedido !== null,
  };
}

const MENSAGEM_POR_MOTIVO: Partial<Record<string, string>> = {
  banco_ativo_requerido: t.erroBancoAtivo,
  escritorio_requerido: t.erroEscritorioRequerido,
  escritorio_nao_vinculado: t.erroEscritorioNaoVinculado,
  banco_sem_escritorio: t.erroBancoSemEscritorio,
  servico_do_escritorio: t.erroServicoDoEscritorio,
  cpf_invalido: t.identificacao.cpfInvalido,
  origem_invalida: t.origem.erroInvalida,
};

export function mensagemDoErroAoAbrir(err: unknown): string {
  return (err instanceof CaseError ? MENSAGEM_POR_MOTIVO[err.reason] : undefined) ?? t.erroAoAbrir;
}

export function pedeEscritorio(err: unknown): boolean {
  return err instanceof CaseError && err.reason === 'escritorio_requerido';
}

/**
 * Grava no processo do caso a identificação e o pedido, só o que mudou. No exame de um processo
 * existente a identificação é a do processo e não muda por aqui.
 */
export async function gravarPedidoNoProcesso(
  client: AxiosInstance,
  caso: CaseRecordData,
  formulario: FormularioDoEnvio,
  processoLido?: ProcessoData,
): Promise<ProcessoData | null> {
  if (caso.processo_id === null || caso.processo_id === undefined) return null;
  const processo = processoLido ?? (await lerProcesso(client, caso.processo_id)).processo;
  const mudancas = mudancasDoProcesso(processo, formulario.identificacao, formulario.pedido);
  if (mudancas === null) return processo;
  return (await atualizarProcesso(client, processo.id, mudancas)).processo;
}
